import React, {useEffect, useState, useCallback} from 'react';
import {
  Expressions,
  matchExpression,
  ExpressionPath,
} from '@packages/systems/wfdl';
import {updateExpressionAction} from '@packages/systems/ExpressionEditor-utils';
import {
  TRecord,
  TBoolean,
  TList,
  TListOfElement,
  TText,
  EList,
  ERecord,
  EText,
  EBoolean,
} from '@packages/systems/wfdl-ast';
import {concat, emptyArray, identity} from '@packages/utilities/fp/utils';
import {
  removeElementById,
  getKeyPathToElement,
  findElementDescendant,
} from '@packages/utilities/platform';
import {
  isUserFormCheckboxInput,
  isUserFormTermsOfService,
  isUserFormTextInput,
  isUserFormPasswordInput,
  isUserFormEmailInput,
  isUserFormBlockLabel,
  isUserFormInput,
  isUserFormNumberInput,
  isUserFormSelectInput,
  isFormCheckboxWrapper,
  isUserFormFileInput,
  isUserFormNameInput,
} from '@packages/utilities/elementTypeUtils';
import {
  isUserFormCheckboxWrapper,
  isUserFormFileUploadWrapper,
} from '@packages/utilities/platform-elements';
import {TEMP_PATH} from '@packages/utilities/expression-path-constants';
import {getNodeClickedActionCreatorFromState} from '@packages/systems/designer/canvas';
import isEqual from 'lodash/isEqual';

import {
  DEFAULT_USER_FIELDS,
  KEY_FROM_RESERVED_USER_FIELD,
} from '@packages/systems/users/constants';
import type {Expression, ExpressionElement} from '@packages/systems/wfdl-ast';
import UserAddFields from './UserAddFields';

import type {
  SiteUsersState,
  UserField,
  UserFieldsStoreType,
  UserFieldType,
} from '@packages/systems/users/flux';
import {
  type ContextualExpression,
  ContextualExpressions,
  stripAll,
  stripAnn,
} from '@packages/utilities/contextual-expression';
import {createIntegratedRootAlias} from '@packages/utilities/integrated-types';

import {USYS_FIELD_PATH} from '@packages/systems/users/usysWfdl';
import {
  useFluxContext,
  useDispatch,
  useDesignerSelector,
} from '@packages/systems/designer/flux';

const getUserFieldState = (state: SiteUsersState): UserFieldsStoreType =>
  state.userFields;

const selectSavedUserFields = (state: any): UserField[] =>
  getUserFieldState(state.SiteUsersStore).savedFields;

const selectedNodeNativeIdSelector = (state: any) =>
  state.UiNodeStore.selectedNodeNativeId;

const FORM_CHILDREN_PATH: ExpressionPath = [{in: 'Record', at: 'children'}];
const USER_FIELD_PATH: ExpressionPath = [
  {in: 'Record', at: 'users'},
  {in: 'Record', at: 'field'},
];

const getTempMenu = (expr: Expression) => {
  const listExpr = Expressions.getIn(expr, TEMP_PATH);
  if (listExpr === undefined) return [];
  const listItems = Expressions.getList(listExpr);
  if (listItems === null) return [];
  return listItems;
};

const removeFieldFromTempMenu = (
  expr: Expression,
  removedElementId: string
): Expression => {
  const listItems = getTempMenu(expr);
  const newList = listItems.filter((item) => {
    const elementId = Expressions.getRecord(item)?.elementId;
    if (elementId == null) return true;

    return Expressions.getText(elementId) !== removedElementId;
  });
  return Expressions.setIn(expr, TEMP_PATH, EList(newList));
};

const createInputRecord = ({
  name,
  fieldId,
  elementId,
  fieldType,
  deletable,
  custom,
}: InputDataPOJO): Expression => {
  return ERecord({
    name: EText(name),
    fieldId: EText(fieldId),
    elementId: EText(elementId),
    fieldType: EText(fieldType),
    deletable: EBoolean(deletable),
    custom: EBoolean(custom),
  });
};
const createInputRecordWithElementId =
  (inputData: ChildrenInputData[]) =>
  ({
    name,
    fieldId,
    elementId,
    fieldType,
    deletable,
    custom,
  }: InputDataPOJO): Expression => {
    return createInputRecord({
      name,
      fieldId,
      elementId:
        inputData.find((data) => data.fieldId === fieldId)?.elementId ||
        elementId,
      fieldType,
      deletable,
      custom,
    });
  };

const getField = (expr: Expression): string => {
  const fieldExpr = Expressions.getIn(expr, [
    {in: 'Element'},
    ...USYS_FIELD_PATH,
  ]);

  if (fieldExpr === undefined) return '';
  const fieldStr = Expressions.getText(fieldExpr);
  if (fieldStr === null) return '';

  return KEY_FROM_RESERVED_USER_FIELD[fieldStr] || fieldStr;
};

const getElementId = (expr: Expression): string => {
  const element = Expressions.getElement(expr);
  if (element === null) return '';
  return Expressions.getElementId(element);
};

const commonFieldToInputPOJO = ({
  name,
  id,
  type,
  elementId,
}: {
  name: string;
  id: string;
  type: UserFieldType;
  elementId?: string;
}): InputDataPOJO => ({
  name,
  fieldId: id,
  elementId: elementId || '',
  fieldType: type,
  deletable: !NON_DELETABLE_FIELDS_IDENTIFIERS.includes(id),
  custom: false,
});

const customFieldToInputPOJO = ({
  name,
  id,
  type,

  // @ts-expect-error dear flow elementId doesnt exist in UserField but does exist in the alternative
  elementId,
}:
  | {
      name: string;
      id: string;
      type: UserFieldType;
      elementId?: string;
    }
  | UserField): InputDataPOJO => {
  return {
    name,
    fieldId: id,
    elementId: elementId || '',
    fieldType: type,
    deletable: true,
    custom: true,
  };
};

type ChildrenInputDataPrelim = {
  expr: Expression;
  fieldId: string | null;
  fieldType: string;
};

type ChildrenInputData = {
  expr: Expression;
  fieldId: string;
  fieldType: string;
  elementId: string;
};

const getFieldIds = ({
  expr,
  fieldId,
  fieldType,
}: ChildrenInputDataPrelim): ChildrenInputData => {
  return {
    expr,
    fieldType,
    fieldId: getField(expr) || fieldId || '',
    elementId: getElementId(expr),
  };
};

const isInputElement = (expr: Expression) => {
  const e = Expressions.getElement(expr);
  if (e === null) return Expressions.resume(emptyArray);
  if (isUserFormTermsOfService(e)) {
    return Expressions.resume([
      {
        expr,
        fieldId: 'acceptPrivacy',
        fieldType: 'Bool',
      },
    ]);
  }
  if (isUserFormCheckboxInput(e)) {
    return Expressions.resume([{expr, fieldId: null, fieldType: 'Bool'}]);
  }
  if (isUserFormPasswordInput(e)) {
    return Expressions.resume([
      {expr, fieldId: 'password', fieldType: 'Password'},
    ]);
  }
  if (isUserFormEmailInput(e)) {
    return Expressions.resume([{expr, fieldId: 'email', fieldType: 'Email'}]);
  }
  if (isUserFormTextInput(e)) {
    return Expressions.resume([{expr, fieldId: null, fieldType: 'PlainText'}]);
  }
  if (isUserFormNumberInput(e)) {
    return Expressions.resume([{expr, fieldId: null, fieldType: 'Number'}]);
  }
  if (isUserFormSelectInput(e)) {
    return Expressions.resume([{expr, fieldId: null, fieldType: 'Option'}]);
  }
  if (isUserFormFileInput(e)) {
    return Expressions.resume([{expr, fieldId: null, fieldType: 'FileRef'}]);
  }
  return Expressions.resume(emptyArray);
};

const getChildUsysFields = (expr: Expression): ChildrenInputData[] =>
  Expressions.everything(concat)(isInputElement)(expr).map(getFieldIds);

const getCorrespondingLabelId = (
  root: Expression,
  inputElementField: undefined | string
) =>
  Expressions.everything(concat)(
    matchExpression({
      Element: (el) => {
        if (!isUserFormBlockLabel(el)) return Expressions.resume([]);
        const userFieldAttr = Expressions.getElementDataAt(
          el,
          USER_FIELD_PATH,
          'Text',
          ''
        );
        if (userFieldAttr !== '' && userFieldAttr === inputElementField) {
          return Expressions.stop([Expressions.getElementId(el)]);
        }
        return Expressions.resume([]);
      },
      _: () => {
        return Expressions.resume([]);
      },
    })
  )(root)[0];

const willShowUserFormSettings = (element: ExpressionElement) =>
  isUserFormTextInput(element) ||
  isUserFormEmailInput(element) ||
  isUserFormNameInput(element) ||
  isUserFormPasswordInput(element) ||
  isUserFormSelectInput(element) ||
  isUserFormCheckboxWrapper(element) ||
  isUserFormFileUploadWrapper(element);

const allUserInputsMatcher = matchExpression({
  Element: (el) => {
    if (willShowUserFormSettings(el)) {
      return Expressions.resume([Expressions.getElementId(el)]);
    } else {
      return Expressions.resume([]);
    }
  },
  _: () => {
    return Expressions.resume([]);
  },
});

const getAllUserInputs = (root: Expression) =>
  Expressions.everything(concat)(allUserInputsMatcher)(root);

export type InputDataPOJO = {
  name: string;
  fieldType: string;
  fieldId: string;
  elementId: string;
  custom: boolean;
  deletable: boolean;
};

const NON_DELETABLE_FIELDS_IDENTIFIERS = ['email', 'password'];

const commonFields = DEFAULT_USER_FIELDS;

const convertToPojo = (menuListExpr: ContextualExpression): InputDataPOJO[] => {
  const list = Expressions.getList(stripAll(menuListExpr)) || [];
  return list.map((item) => {
    const record = Expressions.getRecord(item);
    if (record === null) {
      return {
        name: '',
        fieldId: '',
        elementId: '',
        fieldType: '',
        deletable: false,
        custom: false,
      };
    }
    return {
      // @ts-expect-error - TS2345 - Argument of type 'Expression | undefined' is not assignable to parameter of type 'ExpressionF<Expression>'.
      name: Expressions.getText(record.name) || '',

      // @ts-expect-error - TS2345 - Argument of type 'Expression | undefined' is not assignable to parameter of type 'ExpressionF<Expression>'.
      fieldId: Expressions.getText(record.fieldId) || '',

      // @ts-expect-error - TS2345 - Argument of type 'Expression | undefined' is not assignable to parameter of type 'ExpressionF<Expression>'.
      elementId: Expressions.getText(record.elementId) || '',

      // @ts-expect-error - TS2345 - Argument of type 'Expression | undefined' is not assignable to parameter of type 'ExpressionF<Expression>'.
      fieldType: Expressions.getText(record.fieldType) || '',

      // @ts-expect-error - TS2345 - Argument of type 'Expression | undefined' is not assignable to parameter of type 'ExpressionF<Expression>'.
      deletable: Expressions.getBoolean(record.deletable) || false,

      // @ts-expect-error - TS2345 - Argument of type 'Expression | undefined' is not assignable to parameter of type 'ExpressionF<Expression>'.
      custom: Expressions.getBoolean(record.custom) || false,
    };
  });
};

export type SelectTargetIdMap = {
  [key: string]: (arg1: string) => string;
};

const getInputIdFromContainer =
  (expr: ContextualExpression, pred: (element: ExpressionElement) => boolean) =>
  (elementId: string) => {
    const rootElement = stripAll(expr);
    const containerElement = Expressions.getElementById(rootElement, elementId);
    const input =
      containerElement && findElementDescendant(containerElement, pred);
    if (!input) return elementId;
    return Expressions.getElementId(input) || elementId;
  };

// eslint-disable-next-line webflow/selector-memoization
const selectInputIdFromContainer = (
  expr: ContextualExpression
): SelectTargetIdMap => {
  return {
    PlainText: identity,
    Password: identity,
    Email: identity,
    Bool: getInputIdFromContainer(expr, isUserFormCheckboxInput),
    Number: identity,
    Link: identity,
    Option: identity,
    FileRef: getInputIdFromContainer(expr, isUserFormFileInput),
  };
};

const getContainerId =
  (expr: ContextualExpression, pred: (element: ExpressionElement) => boolean) =>
  (elementId: string) => {
    const rootElement = stripAll(expr);
    const closestContainer = Expressions.closestElement(
      rootElement,
      elementId,
      pred
    );
    if (closestContainer === null) {
      return elementId;
    }
    return Expressions.getElementId(closestContainer) || elementId;
  };

// eslint-disable-next-line webflow/selector-memoization
const selectContainerId = (expr: ContextualExpression): SelectTargetIdMap => {
  return {
    PlainText: identity,
    Password: identity,
    Email: identity,
    Bool: getContainerId(expr, isFormCheckboxWrapper),
    Number: identity,
    Link: identity,
    Option: identity,
    FileRef: getContainerId(expr, isUserFormFileUploadWrapper),
  };
};

const getInputIndex = (inputPath: ExpressionPath) =>
  // @ts-expect-error - TS2339 - Property 'index' does not exist on type 'ExpressionPathPart'.
  inputPath[inputPath.length - 1]?.index;

const getClosestInput = (
  root: Expression,
  elementId: string,
  _getParentListPathRes: undefined,
  deletedElementIds = []
): ExpressionElement | null => {
  const match = (e: null | ExpressionElement) => {
    if (e === null) return false;
    return (
      // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type 'never'.
      !deletedElementIds.includes(Expressions.getElementId(e)) &&
      (isUserFormInput(e) || isUserFormCheckboxWrapper(e))
    );
  };

  const [parentListPath, currentInput] =
    _getParentListPathRes || getParentListPath(root, elementId);
  if (parentListPath === null) return null;
  const siblings = Expressions.getIn(root, parentListPath);
  if (siblings === undefined) return null;
  const siblingsList = Expressions.getList(siblings);
  if (siblingsList === null) return null;

  // @ts-expect-error - TS7034 - Variable 'closestIndex' implicitly has type 'any' in some locations where its type cannot be determined.
  let closestIndex = null;
  const closestInputSibling = siblingsList.reduce<Expression | null>(
    (acc, e, index) => {
      if (match(Expressions.getElement(e))) {
        const relativeIndex = Math.abs(index - (currentInput || 0));
        // @ts-expect-error - TS7005 - Variable 'closestIndex' implicitly has an 'any' type. | TS7005 - Variable 'closestIndex' implicitly has an 'any' type.
        if (closestIndex === null || relativeIndex < closestIndex) {
          closestIndex = relativeIndex;
          return e;
        }
      }
      return acc;
    },
    null
  );

  if (closestInputSibling !== null) {
    return Expressions.getElement(closestInputSibling);
  }

  if (!isEqual(parentListPath, FORM_CHILDREN_PATH)) {
    const directFormChildren = Expressions.getIn(root, FORM_CHILDREN_PATH);
    if (directFormChildren === undefined) return null;
    const directFormChildrenList = Expressions.getList(directFormChildren);
    if (directFormChildrenList === null) return null;
    const matched = directFormChildrenList
      .reverse()

      .find((e) => match(Expressions.getElement(e)));
    if (matched) {
      return Expressions.getElement(matched);
    }
  }

  return null;
};

const getParentListPath = (root: Expression, elementId: string) => {
  const inputPath = getKeyPathToElement(root, elementId);
  if (inputPath === null) return [null, null];
  const inputIndex = getInputIndex(inputPath);
  const parentListPath = inputPath.slice(0, -1);
  return [parentListPath, inputIndex];
};

const updateMenuAndDispatch = (
  expr: Expression,
  dispatch: (arg1?: any) => void,
  elementId: string
) => {
  dispatch(updateExpressionAction(removeFieldFromTempMenu(expr, elementId)));
};

const ATTR_PATH = [{in: 'Record', at: 'attr'}];
export const ATTR_ID_PATH = [...ATTR_PATH, {in: 'Record', at: 'id'}] as const;

const getInputElementField = (root: Expression, elementId: string) => {
  const inputElement = Expressions.getElementById(root, elementId);

  if (!inputElement) {
    console.error('Input not found');
    return;
  }

  return Expressions.getElementDataAt(
    inputElement,
    USER_FIELD_PATH,
    'Text',
    ''
  );
};

const deleteField =
  (
    expr: ContextualExpression,
    dispatch: (arg1?: any) => void,
    onSelectNode: (nativeId: string) => void,
    selectedNodeNativeId: any
  ) =>
  (
    // the elementId could be the container or input
    elementId: string,
    fieldType: string
  ): void => {
    const root = stripAll(expr);
    let inputId = null;

    // @ts-expect-error - TS2532 - Object is possibly 'undefined'. | TS2722 - Cannot invoke an object which is possibly 'undefined'.
    const containerId = selectContainerId(expr)[fieldType](elementId);

    // if elementId is the container, then get input within it
    if (containerId === elementId) {
      // @ts-expect-error - TS2532 - Object is possibly 'undefined'. | TS2722 - Cannot invoke an object which is possibly 'undefined'.
      inputId = selectInputIdFromContainer(expr)[fieldType](elementId);
    }
    const inputElementField = getInputElementField(root, inputId || elementId);
    const labelElementId = getCorrespondingLabelId(root, inputElementField);

    let newExpr = removeElementById(root, containerId);

    if (labelElementId && inputElementField) {
      newExpr = removeElementById(newExpr, labelElementId);
    }

    const allUserInputsIds = getAllUserInputs(root);

    if (
      elementId === selectedNodeNativeId ||
      labelElementId === selectedNodeNativeId ||
      containerId === selectedNodeNativeId
    ) {
      // find closest input
      const indexOfSelectedInput = allUserInputsIds.findIndex(
        (id) => id === selectedNodeNativeId
      );
      if (indexOfSelectedInput === -1) {
        // @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
        onSelectNode(allUserInputsIds[0]);
      }
      const nextIndex = indexOfSelectedInput + 1;
      const prevIndex = indexOfSelectedInput - 1;
      if (nextIndex < allUserInputsIds.length) {
        // @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
        onSelectNode(allUserInputsIds[nextIndex]);
      } else if (prevIndex >= 0) {
        // @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
        onSelectNode(allUserInputsIds[prevIndex]);
      }
    }

    updateMenuAndDispatch(newExpr, dispatch, elementId);
  };

export const UserForm = createIntegratedRootAlias({
  params: ['extraAtomFields'],
  fields: {
    temp: TList(
      TRecord({
        name: TText,
        fieldId: TText,
        elementId: TText,
        fieldType: TText,
        deletable: TBoolean,
        custom: TBoolean,
      })
    ),
    type: TText,
    children: TListOfElement,
  },
  render: function RenderUserForm({expr, dispatch}) {
    const flux = useFluxContext();
    const nodeClicked = getNodeClickedActionCreatorFromState(flux.state);
    const wfDispatch = useDispatch();

    const selectedNodeNativeId = useDesignerSelector(
      selectedNodeNativeIdSelector
    );

    const customFields: UserField[] = useDesignerSelector(
      selectSavedUserFields
    );
    const [inputs, setInputs] = useState<InputDataPOJO[]>([]);

    const menuListExpr = ContextualExpressions.getIn(expr, TEMP_PATH);

    useEffect(
      () => {
        if (menuListExpr === undefined) return;
        // for performance reasons, only update temp from inputs directly when temp is unset
        if (ContextualExpressions.getList(stripAnn(menuListExpr))?.length !== 0)
          return;
        const commonFieldsTemplate = commonFields.map(commonFieldToInputPOJO);
        const customFieldsTemplate = customFields.map(customFieldToInputPOJO);

        const root = stripAll(expr);
        const inputData = getChildUsysFields(root);

        const fieldsExprWithInputIds = [
          ...commonFieldsTemplate,
          ...customFieldsTemplate,
        ].map(createInputRecordWithElementId(inputData));

        const newExpr = Expressions.setIn(
          root,
          TEMP_PATH,
          EList(fieldsExprWithInputIds)
        );
        dispatch(updateExpressionAction(newExpr));
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      []
    );

    useEffect(() => {
      if (menuListExpr !== undefined) {
        setInputs(convertToPojo(menuListExpr));
      }
    }, [menuListExpr]);

    const onSelectNode = useCallback(
      (nativeId: string) => {
        wfDispatch(
          nodeClicked({
            nativeIdInCurrentComponent: nativeId,
          })
        );
      },
      [wfDispatch, nodeClicked]
    );

    return (
      <UserAddFields
        inputs={inputs.filter((input) => input.elementId)}
        deleteField={deleteField(
          expr,
          dispatch,
          onSelectNode,
          selectedNodeNativeId
        )}
        selectContainerId={selectContainerId(expr)}
      />
    );
  },
});

export default UserForm;
